import { Autocomplete, Grid, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useAppDispatch } from "../../../../app/features/store/store";
import {
  ClientInvoiceEditResponse,
  RatecardClientInvoiceResponse,
  RouteResponse,
  TripAndDateRangeDataForSelectedStores,
} from "../../../../app/models/responseModels/ClientInvoiceResponses";
import SubmitModalWithFormik from "../../../../components/common/modalPopup/SubmitModalWithFormik";
import { Formik } from "formik";
import * as Yup from "yup";
import FormikTextField from "../../../../components/common/formikFields/FormikTextField";
import { useSubmitModal } from "../../../../components/common/modalPopup/useSubmitModal";
import { toast } from "react-toastify";
import getAxiosError from "../../../../app/api/axiosError";
import EditInvoiceDetailsModal from "./EditInvoiceDetailsModal";
import { ClientInvoiceAgent } from "../../../../app/api/clientInvoices/ClientInvoiceAgent";
import { formatDate } from "../../../../components/common/utils/DateUtils";
import dayjs, { Dayjs } from "dayjs";
import Loader from "../../../../components/common/loader/Loader";
import { getMinOrMaxDate } from "../utils/ClientInvoiceUtils";
import ClientInvoiceEditList from "./ClientInvoiceEditList";

interface InvoiceEditProps {
  clientId: number;
  invoice: RatecardClientInvoiceResponse | undefined;
  closeInvoiceEditModal: CallableFunction;
  onAddMoreStores: CallableFunction;
}

interface InvoiceEdit {
  stores: number[];
  storeSelectType: string;
  start_date: Dayjs | undefined;
  end_date: Dayjs | undefined;
  route: number | undefined;
}

const InvoiceEditModal = (props: InvoiceEditProps) => {
  const dispatch = useAppDispatch();

  const initialValues: InvoiceEdit = {
    stores: [] as number[],
    storeSelectType: "single",
    start_date: undefined,
    end_date: undefined,
    route: undefined,
  };

  const validationSchema = Yup.object().shape({
    stores: Yup.array()
      .min(1, "Please select atleast 1 store")
      .required("Please select a store"),
    end_date: Yup.date().when("start_date", (start_date, schema) => {
      if (start_date != null && start_date[0] != null)
        return schema
          .required("Please select the end date")
          .min(Yup.ref("start_date"), "End date must be after start date");
      return schema.optional();
    }),
  });

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [selectedStores, setSelectedStores] = useState<string>();
  const [startDate, setStartDate] = useState<any>();
  const [endDate, setEndDate] = useState<any>();
  const [selectedRoute, setSelectedRoute] = useState<RouteResponse | null>(
    null
  );

  const [editList, setEditList] = useState<ClientInvoiceEditResponse[]>([]);
  const [deleteInvoiceEditIds, setDeleteInvoiceEditIds] = useState<number[]>(
    []
  );

  const [selectedRatecardId, setSelectedRatecardId] = useState<number>();
  const [currentEditId, setCurrentEditId] = useState<number | null>(null);

  const [routes, setRoutes] = useState<RouteResponse[]>([]);

  const [startRange, setStartRange] = useState<any>();
  const [endRange, setEndRange] = useState<any>();

  // const [stores, setStores] = useState<StoreResponseForClientInvoice[]>([]);

  const [tripAndDateRangeData, setTripAndDateRangeData] = useState<
    TripAndDateRangeDataForSelectedStores[]
  >([]);

  const fetchEditList = async () => {
    if (props.invoice?.clientInvoiceId) {
      try {
        const response = await ClientInvoiceAgent.getEditList(
          props.invoice?.clientInvoiceId
        );

        if (!response.isSuccess) {
          toast.error(response.message, {
            toastId: "getEditDataById",
          });
          return;
        } else {
          setEditList(response.data);
        }
      } catch (error) {
        const appError = getAxiosError(error);
        toast.error(appError.errorMessage, {
          toastId: "getRatecardByIdException",
        });
      } finally {
        setIsLoading(false);
      }
    }
  };

  const fetchTripAndDateRangeForSelectedStores = async () => {
    try {
      if (props.invoice && selectedStores) {
        const response =
          await ClientInvoiceAgent.getTripAndDateRangeForSelectedStores(
            props.invoice.clientInvoiceId,
            selectedStores
          );

        setTripAndDateRangeData(response.data);

        if (response.data && response.data.length > 0) {
          const r: RouteResponse[] = response.data.map((item) => item.route);
          console.log(`routes = ${JSON.stringify(r)}`);
          setRoutes(r);

          const startDateRangeList = response.data.map((item) =>
            dayjs(item.startDateRange)
          );

          const endDateRangeList = response.data.map((item) =>
            dayjs(item.endDateRange)
          );

          setStartRange(getMinOrMaxDate(startDateRangeList, true));
          setEndRange(getMinOrMaxDate(endDateRangeList, false));
        }

        if (!response.isSuccess) {
          toast.error(response.message, {
            toastId: "tripAndDateRangeData",
          });
          return;
        }
      }
    } catch (error) {
      const appError = getAxiosError(error);
      toast.error(appError.errorMessage, {
        toastId: "tripAndDateRangeDataException",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchRouteByTripId = async (tripId: string) => {
    try {
      if (props.invoice) {
        const response = await ClientInvoiceAgent.getGenericRouteByTripId(
          props.invoice?.id,
          tripId
        );

        if (response.isSuccess && response.data) {
          console.log(
            `fetchRouteByTripId = selected route = ${JSON.stringify(
              response.data
            )}`
          );
          setSelectedRoute(response.data);
        } else {
          toast.error(response.message, {
            toastId: "getRouteByTripIdException",
          });
          return;
        }
      }
    } catch (error) {
      const appError = getAxiosError(error);
      toast.error(appError.errorMessage, {
        toastId: "getRouteByTripIdException",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenEdit = (
    currentEditId: number,
    store: number,
    tripId: string | null
  ) => {
    // when we select an existing to edit on we need to fill these for the details modal to work
    setCurrentEditId(currentEditId);
    setSelectedStores(`${store}`);

    // we get store id from the list and we find the ratecard id from the list of stores we have
    const rcId = props.invoice?.stores.find((s) => s.id === store)?.ratecardId;
    setSelectedRatecardId(rcId);

    if (tripId) fetchRouteByTripId(tripId);

    openEditInvoiceDetailsModal();
  };

  const handleDeleteInvoiceEdit = async () => {
    try {
      if (props.invoice) {
        const response = await ClientInvoiceAgent.deleteInvoiceEdit(
          props.invoice?.clientInvoiceId,
          deleteInvoiceEditIds.join(","),
          null
        );

        console.log(`handleDeleteInvoiceEdit`);
        console.log(`response = ${JSON.stringify(response)}`);

        if (response.isSuccess) {
          console.log(`refresh edit list`);
          // refresh edit list
          setEditList(response.data);
        } else {
          toast.error(response.message, {
            toastId: "tripAndDateRangeData",
          });
        }
      }
    } catch (error) {
      const appError = getAxiosError(error);
      toast.error(appError.errorMessage, {
        toastId: "tripAndDateRangeDataException",
      });
    } finally {
      setIsLoading(false);
    }
    return true;
  };

  // ***** UseEffects *****
  useEffect(() => {
    fetchEditList();
  }, []);

  // fetch routes and date range stores are selected
  useEffect(() => {
    if (selectedStores) {
      setIsLoading(true);
      fetchTripAndDateRangeForSelectedStores();
    }
  }, [selectedStores]);

  // open edit invoice modal logic
  const [editInvoiceDetailsModal, openEditInvoiceDetailsModal] = useSubmitModal(
    (closeEditInvoiceDetailsModal) =>
      selectedRatecardId ? (
        <EditInvoiceDetailsModal
          store={selectedStores ?? ""}
          clientId={props.clientId}
          startDate={startDate ? formatDate(startDate.toDate()) : null}
          endDate={endDate ? formatDate(endDate.toDate()) : null}
          route={selectedRoute}
          ratecardId={selectedRatecardId}
          closeEditInvoiceDetailsModal={props.onAddMoreStores}
          //   closeEditInvoiceDetailsModal={() => {
          //   closeEditInvoiceDetailsModal();
          //   props.closeInvoiceEditModal();
          // }}
          invoice={props.invoice}
          onCompleted={() => {
            props.closeInvoiceEditModal();
          }}
          onAddNewStores={props.onAddMoreStores}
          currentEditId={currentEditId}
          isFdpInvoiceEdit={false}
          fdpInvoiceId={null}
          fdpInvoiceStatus={null}
        />
      ) : undefined
  );

  return (
    <>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={() => {
          openEditInvoiceDetailsModal();
        }}
      >
        {({
          values,
          setFieldValue,
          isValid,
          errors,
          handleChange,
          handleBlur,
        }) => {
          // console.log(`values = ${JSON.stringify(values)}`);
          // console.log(`errors = ${JSON.stringify(errors)}`);
          // console.log(
          //   `startRange = ${JSON.stringify(
          //     startRange
          //   )} and endRange = ${JSON.stringify(endRange)}`
          // );
          console.log(`edit list = ${JSON.stringify(editList)}`);

          return (
            <SubmitModalWithFormik
              title="Edit client invoice"
              closeSubmitModal={props.closeInvoiceEditModal}
              childErrorMessage={""}
              allowSubmit={false}
              size="lg"
              isLoading={false}
              submitButtonName="Edit"
              closeModalAfterSubmit={false}
              fullWidth={true}
            >
              <Grid
                container
                rowSpacing={2}
                columnSpacing={2}
                alignItems={"center"}
              >
                <Loader isLoading={isLoading} />

                {/* <Grid item xs={12} sm={4} paddingTop={1}>
                  <RadioGroup
                    row
                    id="storeSelectType"
                    name="storeSelectType"
                    value={values.storeSelectType}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  >
                    <FormControlLabel
                      value="single"
                      control={<Radio />}
                      label="Single Store"
                    />
                    <FormControlLabel
                      value="multi"
                      control={<Radio />}
                      label="Multi Stores"
                    />
                  </RadioGroup>
                </Grid> */}

                <Grid item xs={values.stores.length > 1 ? 4 : 3}>
                  <Autocomplete
                    multiple
                    id="stores"
                    options={props.invoice?.stores ?? []}
                    limitTags={3}
                    disableCloseOnSelect
                    size="small"
                    onChange={(event, value) => {
                      setFieldValue(
                        "stores",
                        value.map((x) => x.id)
                      );

                      // if no option is selected then revert selectedRatecardId to undefined
                      if (!value || !value[0]) {
                        setSelectedRatecardId(undefined);
                      }

                      // if selecting the first store in the list then set selectedRatecardId so that stores of other ratecards are disabled
                      if (
                        values.stores.length === 0 &&
                        value &&
                        value[0].ratecardId
                      )
                        setSelectedRatecardId(value[0].ratecardId);

                      // convert selected store values to string of comma-separated store ids to be used for fetching the routes for these stores
                      const storeList = value.map((x) => x.id).toString();
                      setSelectedStores(storeList);
                      // TODO: clear route field on store select

                      setRoutes([]);
                    }}
                    value={props.invoice?.stores.filter((x) =>
                      values.stores?.includes(x.id)
                    )}
                    getOptionDisabled={(option) => {
                      // select store that belong to same ratecard only
                      // disable other stores
                      if (
                        selectedRatecardId &&
                        option.ratecardId !== selectedRatecardId
                      )
                        return true;
                      return false;
                    }}
                    getOptionLabel={(option) => `${option.storeId}`}
                    sx={{
                      // overring font-family else it chose different font-family on its own!
                      fontFamily: `-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol"`,
                    }}
                    renderInput={(params) => (
                      <FormikTextField
                        {...params}
                        margin="normal"
                        name="stores"
                        label="Select stores to edit"
                        size="small"
                        fullWidth
                        variant={"outlined"}
                        required
                      />
                    )}
                  />
                </Grid>
                <Grid
                  item
                  xs={4}
                  display={values.stores.length > 1 ? "none" : ""}
                >
                  <Autocomplete
                    id="route"
                    options={routes}
                    size="small"
                    disabled={values.stores.length === 0}
                    getOptionLabel={(option) => `${option.tripId}`}
                    sx={{
                      // overring font-family else it chose different font-family on its own!
                      fontFamily: `-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol"`,
                    }}
                    onChange={(event, value) => {
                      setFieldValue("route", value?.id);
                      setSelectedRoute(value);
                    }}
                    value={routes.find((x) => values.route === x.id)}
                    renderInput={(params) => (
                      <FormikTextField
                        {...params}
                        margin="normal"
                        name="route"
                        label="Select a route to edit"
                        size="small"
                        fullWidth
                        variant="outlined"
                        slotprops={{
                          input: {
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                <Loader isLoading={true} />
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          },
                        }}
                      />
                    )}
                  />
                </Grid>

                <>
                  <Grid item container>
                    <Typography component="h4" variant="h4">
                      Edit List
                    </Typography>
                  </Grid>
                  <Grid item>
                    <ClientInvoiceEditList
                      editList={editList}
                      isClientIdColumnVisible={false}
                      handleOpenEdit={handleOpenEdit}
                      handleDeleteInvoiceEdit={handleDeleteInvoiceEdit}
                      setDeleteInvoiceEditIds={setDeleteInvoiceEditIds}
                      invoice={props.invoice}
                    />
                  </Grid>
                </>
              </Grid>
            </SubmitModalWithFormik>
          );
        }}
      </Formik>
      {editInvoiceDetailsModal}
    </>
  );
};

export default InvoiceEditModal;
